import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { rcsApi } from '@/api/rcs.api';
import { extractErrorMessage } from '@/lib/errors';
import type { RcsCampaignStatus, RcsDispatchStatus, RcsTemplateType } from '@/types/api.types';

// ─── Status ────────────────────────────────────────────────────────────────

export function useRcsEnabled() {
  return useQuery({
    queryKey: ['rcs', 'status'],
    queryFn: () => rcsApi.getStatus(),
    select: (res) => res.data.data.enabled,
  });
}

// ─── Config ────────────────────────────────────────────────────────────────

export function useRcsConfig() {
  return useQuery({
    queryKey: ['rcs', 'config'],
    queryFn: () => rcsApi.getConfig(),
    select: (res) => res.data.data,
  });
}

export function useUpsertRcsConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: rcsApi.upsertConfig,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rcs', 'config'] });
      toast.success('Configuração RCS salva');
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

export function useRegisterRcsWebhook() {
  return useMutation({
    mutationFn: rcsApi.registerWebhook,
    onSuccess: (res) => {
      const { action } = res.data.data;
      toast.success(
        action === 'already_registered'
          ? 'Webhook RCS já estava registrado'
          : 'Webhook RCS registrado na Zenvia',
      );
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

// ─── Templates ─────────────────────────────────────────────────────────────

export function useRcsTemplatesList(params?: { type?: RcsTemplateType; isActive?: boolean }) {
  return useQuery({
    queryKey: ['rcs', 'templates', params],
    queryFn: () => rcsApi.listTemplates({ pageSize: 100, ...params }),
    select: (res) => res.data,
  });
}

export function useCreateRcsTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: rcsApi.createTemplate,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rcs', 'templates'] });
      toast.success('Template RCS criado com sucesso');
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

export function useUpdateRcsTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof rcsApi.updateTemplate>[1] }) =>
      rcsApi.updateTemplate(id, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rcs', 'templates'] });
      toast.success('Template RCS atualizado');
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

export function useDeleteRcsTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: rcsApi.deleteTemplate,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rcs', 'templates'] });
      toast.success('Template RCS removido');
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

export function useSyncRcsTemplates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: rcsApi.syncTemplatesFromZenvia,
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['rcs', 'templates'] });
      const { imported, updated, removed } = res.data.data;
      const parts = [`${imported} importado(s)`, `${updated} atualizado(s)`];
      if (removed > 0) parts.push(`${removed} removido(s)`);
      toast.success(parts.join(', '));
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

// ─── Campaigns ─────────────────────────────────────────────────────────────

export function useRcsCampaignsList(params?: {
  page?: number;
  pageSize?: number;
  status?: RcsCampaignStatus;
}) {
  return useQuery({
    queryKey: ['rcs', 'campaigns', 'list', params],
    queryFn: () => rcsApi.listCampaigns(params),
    select: (res) => res.data,
  });
}

export function useRcsCampaignDetail(id: string) {
  return useQuery({
    queryKey: ['rcs', 'campaigns', 'detail', id],
    queryFn: () => rcsApi.getCampaign(id),
    select: (res) => res.data.data,
    enabled: !!id,
    refetchInterval: 15_000,
  });
}

export function useCreateRcsCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: rcsApi.createCampaign,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rcs', 'campaigns'] });
      toast.success('Campanha RCS criada com sucesso');
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

export function useStartRcsCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => rcsApi.startCampaign(id),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: ['rcs', 'campaigns'] });
      void qc.invalidateQueries({ queryKey: ['rcs', 'campaigns', 'detail', id] });
      toast.success('Campanha RCS iniciada');
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

export function useCancelRcsCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => rcsApi.cancelCampaign(id),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: ['rcs', 'campaigns'] });
      void qc.invalidateQueries({ queryKey: ['rcs', 'campaigns', 'detail', id] });
      toast.success('Campanha RCS cancelada');
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

// ─── Dispatches ────────────────────────────────────────────────────────────

export function useRcsDispatchesList(params?: {
  page?: number;
  pageSize?: number;
  status?: RcsDispatchStatus;
  campaignId?: string;
}) {
  return useQuery({
    queryKey: ['rcs', 'dispatches', params],
    queryFn: () => rcsApi.listDispatches(params),
    select: (res) => res.data,
  });
}

export function useQuickSendRcs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: rcsApi.sendQuick,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rcs', 'dispatches'] });
      toast.success('Mensagem RCS enviada com sucesso');
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

export function useRetryRcsDispatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: rcsApi.retryDispatch,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rcs', 'dispatches'] });
      toast.success('Reenvio agendado');
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

export function useRcsDispatchStats(period: '7d' | '30d' | '90d' = '30d') {
  return useQuery({
    queryKey: ['rcs', 'dispatch-stats', period],
    queryFn: () => rcsApi.getDispatchStats(period),
    select: (res) => res.data.data,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useRcsDispatchLogs(dispatchId: string | null) {
  return useQuery({
    queryKey: ['rcs', 'dispatch-logs', dispatchId],
    queryFn: () => rcsApi.getDispatchLogs(dispatchId!),
    select: (res) => res.data.data,
    enabled: !!dispatchId,
  });
}
